import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Outlet } from 'react-router-dom'
import { toast } from 'react-toastify'
import AppSidebar from './AppSidebar'
import AttendancePunchModal, { shouldShowAttendanceModal, markShownToday } from './AttendancePunchModal'
import InactivityWarningModal from './rr/InactivityWarningModal'
import socketService from '@/services/socketService'
import { useUser } from '@/contexts/UserContext'
import { AuthService } from '@/services/authService'
import { rrPunch, rrGetAttendanceToday } from '@/actions/rr'

const INACTIVITY_MS = 8 * 60 * 1000   // 8 min idle before showing modal
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll']

// Punch out if currently punched in, then full logout + redirect
async function punchOutAndLogout() {
  try {
    const res = await rrGetAttendanceToday()
    const td = res?.data as { status?: string; punch_out?: string | null } | null
    const punchedIn = td?.status === 'present' && !td?.punch_out
    if (punchedIn) await rrPunch()
  } catch { /* best-effort */ }
  sessionStorage.removeItem('zh:attendance:punch_shown')
  const redirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login'
  try {
    await AuthService.logout({ clearLocalStorage: true, clearSessionStorage: false, clearIndexedDB: false, notifyBackend: true, redirectTo: redirectUrl })
  } catch {
    window.location.href = redirectUrl
  }
}

const Layout: React.FC = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(
    () => localStorage.getItem('zh:sidebar:expanded') === 'true'
  );
  const [showAttendance, setShowAttendance] = useState(false)
  const [showInactivity, setShowInactivity] = useState(false)
  const { userData } = useUser()
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Inactivity tracking ─────────────────────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    if (showInactivity) return  // Don't reset while modal is showing
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    inactivityTimer.current = setTimeout(() => setShowInactivity(true), INACTIVITY_MS)
  }, [showInactivity])

  useEffect(() => {
    resetInactivityTimer()
    const handler = () => resetInactivityTimer()
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }))
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handler))
    }
  }, [resetInactivityTimer])

  function handleInactivityDismiss() {
    setShowInactivity(false)
    resetInactivityTimer()
  }

  function handleInactivityTimeout() {
    setShowInactivity(false)
    punchOutAndLogout()   // punch out first, then logout
  }

  // ── Attendance check on load ────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(async () => {
      const show = await shouldShowAttendanceModal()
      if (show) setShowAttendance(true)
    }, 800)
    return () => clearTimeout(timer)
  }, [])

  // ── End-of-shift auto punch-out ─────────────────────────────────────────
  // Polls every minute; once current time passes closing hour (20:00 = 8 PM)
  // and the agent is still punched in, auto punch-out silently.
  useEffect(() => {
    const CLOSING_HOUR = 20  // 8 PM — matches default rr_working_hours closes

    async function checkShiftEnd() {
      const now = new Date()
      if (now.getHours() < CLOSING_HOUR) return
      try {
        const res = await rrGetAttendanceToday()
        const d = res?.data as { status?: string; punch_in?: string | null; punch_out?: string | null } | null
        if (d?.status === 'present' && d?.punch_in && !d?.punch_out) {
          await rrPunch()   // auto punch-out at shift end
          toast.info('Shift ended — you have been automatically punched out.', { autoClose: 6000 })
        }
      } catch { /* silent */ }
    }

    // Check immediately (handles refresh after 8 PM) then every 60s
    checkShiftEnd()
    const interval = setInterval(checkShiftEnd, 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    socketService.connect();
    setupNotificationListeners();
    return () => { socketService.disconnect(); };
  }, []);

  const setupNotificationListeners = () => {
    socketService.on('new_lead', (data: any) => {
      toast.success(
        <div>
          <strong>New Lead Assigned!</strong>
          <div className="text-sm mt-1">{data.lead_name} from {data.source || 'Unknown source'}</div>
        </div>,
        {
          autoClose: 5000,
          onClick: () => { window.location.href = `/leads?lead_id=${data.lead_id}`; }
        }
      );
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('New Lead Assigned!', {
          body: `${data.lead_name} from ${data.source || 'Unknown source'}`,
          icon: '/assets/dino.png',
          tag: `lead-${data.lead_id}`,
        });
      }
    });

    socketService.on('reminder_due', (data: any) => {
      toast.warning(
        <div>
          <strong>Reminder Alert!</strong>
          <div className="text-sm mt-1">{data.event_title}</div>
          <div className="text-xs text-gray-600 mt-1">{data.event_date_time}</div>
        </div>,
        {
          autoClose: 8000,
          onClick: () => { window.location.href = '/activities'; }
        }
      );
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Reminder Alert!', {
          body: `${data.event_title} - ${data.event_date_time}`,
          icon: '/assets/dino.png',
          tag: `reminder-${data.event_id}`,
        });
      }
    });

    socketService.on('lead_status_changed', (data: any) => {
      toast.info(`Lead status updated: ${data.lead_name} → ${data.new_status}`);
    });
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      <AppSidebar isExpanded={sidebarExpanded} setIsExpanded={setSidebarExpanded} />
      {/* pl-[52px] always reserves space for the collapsed rail; expanded sidebar floats over content */}
      <main
        className="pl-[52px] h-screen overflow-y-auto"
        onClick={() => { if (sidebarExpanded) setSidebarExpanded(false); }}
      >
        <Outlet />
      </main>

      {showAttendance && (
        <AttendancePunchModal
          userName={userData?.data?.userName ?? 'there'}
          onClose={() => {
            markShownToday()
            setShowAttendance(false)
          }}
        />
      )}


      {showInactivity && (
        <InactivityWarningModal
          durationSeconds={120}
          onDismiss={handleInactivityDismiss}
          onTimeout={handleInactivityTimeout}
        />
      )}
    </div>
  );
};

export default Layout;
